/**
 * Initial Loader Manager
 *
 * Centralized utility for managing the initial HTML loader (#initial-loader).
 * This prevents code duplication and race conditions from direct DOM manipulation
 * spread across multiple components.
 *
 * The initial loader exists outside React's root and requires direct DOM access.
 * This utility provides a clean API for showing, hiding, and cleaning up the loader.
 */

// Duration must match CSS transition in index.html (.initial-loader opacity transition)
export const LOADER_FADE_DURATION = 700; // milliseconds

// Delay before hiding loader to allow app content to start rendering
// Prevents blank flash on slower connections during cross-fade
export const LOADER_HIDE_DELAY = 100; // milliseconds

// Minimum time the loader must be visible for consistent branded experience
// This ensures the loader is seen even on fast loads (e.g., after login)
export const LOADER_MIN_DISPLAY_TIME = 500; // milliseconds

/**
 * Loading milestones with their progress percentages
 * These represent real stages of the app initialization
 */
export const LOADER_MILESTONES = {
  INITIAL: 5,           // Page loaded, script running
  REACT_MOUNTED: 20,    // React app has mounted
  SESSION_VALIDATED: 40, // Auth session validated
  BOOTSTRAP_STARTED: 55, // Started fetching data
  BOOTSTRAP_COMPLETE: 85, // All data fetched
  WORKSPACE_READY: 100  // Ready to show UI
};

/**
 * Set loader progress percentage (0-100)
 * Uses the global __loaderProgress API defined in index.html
 *
 * @param {number} percent - Progress percentage (0-100)
 */
export function setLoaderProgress(percent) {
  if (window.__loaderProgress && typeof window.__loaderProgress.set === 'function') {
    window.__loaderProgress.set(Math.min(100, Math.max(0, percent)));
  }
}

/**
 * Get current loader progress percentage
 *
 * @returns {number} Current progress (0-100)
 */
export function getLoaderProgress() {
  if (window.__loaderProgress && typeof window.__loaderProgress.get === 'function') {
    return window.__loaderProgress.get();
  }
  return 0;
}

/**
 * Set loader to a specific milestone
 * Convenience method for common progress stages
 *
 * @param {string} milestone - Key from LOADER_MILESTONES
 */
export function setLoaderMilestone(milestone) {
  if (LOADER_MILESTONES[milestone] !== undefined) {
    setLoaderProgress(LOADER_MILESTONES[milestone]);
  }
}

/**
 * Show the initial loader
 * Used when redirecting from login to board to maintain seamless experience
 */
export function showInitialLoader() {
  const loader = document.getElementById('initial-loader');
  if (loader) {
    // Track when loader was shown using DOM attribute (avoids module-level state issues with HMR)
    loader.dataset.shownAt = String(Date.now());
    // Reset hide-in-progress flag
    delete loader.dataset.hideInProgress;
    // Reset visibility - remove loaded class and ensure opacity is 1
    loader.classList.remove('loaded');
    loader.style.opacity = '1';
    loader.style.display = 'flex';
    // Reset progress to initial state when showing loader again
    setLoaderProgress(LOADER_MILESTONES.INITIAL);
  }
}

/**
 * Hide the initial loader with fade animation
 * Removes element from DOM after fade completes
 * Respects minimum display time for consistent branded experience
 */
export function hideInitialLoader() {
  const loader = document.getElementById('initial-loader');
  if (!loader) return;

  // Prevent duplicate hide calls (e.g., rapid navigation)
  if (loader.dataset.hideInProgress === 'true') return;
  loader.dataset.hideInProgress = 'true';

  // Calculate remaining time to meet minimum display requirement
  const shownAt = parseInt(loader.dataset.shownAt || '0', 10);
  const elapsed = Date.now() - shownAt;
  const remainingMinTime = Math.max(0, LOADER_MIN_DISPLAY_TIME - elapsed);

  // Delay hide if minimum display time not yet met
  setTimeout(() => {
    const element = document.getElementById('initial-loader');
    if (!element) return;

    // Start fade animation (use both class and inline style for reliability)
    element.classList.add('loaded');
    element.style.opacity = '0';

    // Remove from DOM after fade completes
    setTimeout(() => {
      const el = document.getElementById('initial-loader');
      if (el) {
        el.remove();
      }
    }, LOADER_FADE_DURATION);
  }, remainingMinTime);
}

/**
 * Hide the initial loader immediately without fade
 * Used for public routes where loader should not be visible at all
 */
export function hideInitialLoaderImmediate() {
  const loader = document.getElementById('initial-loader');
  if (loader) {
    loader.style.display = 'none';
  }
}

/**
 * Check if initial loader exists in DOM
 * Useful for conditional logic
 */
export function hasInitialLoader() {
  return document.getElementById('initial-loader') !== null;
}
